/**
 * Bundled copy of the owner-editable content (contract v1.3 §3.11, §3.13)
 * and the small rules every page applies to it, whichever source it came
 * from.
 *
 * The seed JSON in contentSeed/ is what the backend loads into an empty
 * database. Pages render it straight away and while the API is down or slow,
 * in the same shape the API returns: visible items only, in file order, with
 * `seed:` media references expanded into media-object-like values that point
 * at the bundled files (src/assets/grounding/, src/assets/gallery/ and
 * src/assets/downloads/, byte-identical to the seed).
 *
 * @module content
 */
import config from "../config";
import { safeHttpsUrl } from "../services/utils/safeUrl";
import groups from "./contentSeed/groups.json";
import workshops from "./contentSeed/workshops.json";
import privateGroups from "./contentSeed/private_groups.json";
import resources from "./contentSeed/resources.json";
import grounding from "./contentSeed/grounding.json";
import downloads from "./contentSeed/downloads.json";
import faqs from "./contentSeed/faqs.json";
import settings from "./contentSeed/settings.json";
import gallery from "./contentSeed/gallery.json";
import seedMedia from "./contentSeed/media.json";
import grounding54321 from "../assets/grounding/54321-grounding.jpg";
import squareBreathing from "../assets/grounding/square-breathing.jpg";
import rainbowBreathing from "../assets/grounding/rainbow-breathing.jpg";
import butterflyBreathing from "../assets/grounding/butterfly-breathing.jpg";
import gallerySwirlPanels from "../assets/gallery/swirl-panels.jpg";
import galleryCollage from "../assets/gallery/collage.jpg";
import galleryPastelSwirls from "../assets/gallery/pastel-swirls.jpg";
import galleryWatercolourRoses from "../assets/gallery/watercolour-roses.jpg";
import galleryBookmarks from "../assets/gallery/bookmarks.jpg";
import galleryCommunityArtTable from "../assets/gallery/community-art-table.jpg";
import galleryAataConference from "../assets/gallery/aata-conference.jpg";
import galleryAataConferenceGroup from "../assets/gallery/aata-conference-group.jpg";
import galleryArtAndExhalePastels from "../assets/gallery/art-and-exhale-pastels.jpg";
import galleryPaperMaking from "../assets/gallery/paper-making.jpg";
import galleryPaintedGlobe from "../assets/gallery/painted-globe.jpg";
import galleryFeltHearts from "../assets/gallery/felt-hearts.jpg";
import galleryPaintedMask from "../assets/gallery/painted-mask.jpg";
import galleryPaperPortrait from "../assets/gallery/paper-portrait.jpg";
import galleryBeadedKeychains from "../assets/gallery/beaded-keychains.jpg";
import galleryCardboardOctopuses from "../assets/gallery/cardboard-octopuses.jpg";
import galleryTreesPainting from "../assets/gallery/trees-painting.jpg";
import galleryNewspaperCollageGroup from "../assets/gallery/newspaper-collage-group.jpg";
import galleryPaperCrownDrawing from "../assets/gallery/paper-crown-drawing.jpg";
import mySafetyPlan from "../assets/downloads/my-safety-plan.pdf";

const SEED = {
  groups,
  workshops,
  private_groups: privateGroups,
  resources,
  grounding,
  downloads,
  faqs,
  settings,
  gallery,
};

/** Bundled file for each seed media id. */
const SEED_FILES = {
  "seed:grounding/54321-grounding.jpg": grounding54321,
  "seed:grounding/square-breathing.jpg": squareBreathing,
  "seed:grounding/rainbow-breathing.jpg": rainbowBreathing,
  "seed:grounding/butterfly-breathing.jpg": butterflyBreathing,
  "seed:gallery/swirl-panels.jpg": gallerySwirlPanels,
  "seed:gallery/collage.jpg": galleryCollage,
  "seed:gallery/pastel-swirls.jpg": galleryPastelSwirls,
  "seed:gallery/watercolour-roses.jpg": galleryWatercolourRoses,
  "seed:gallery/bookmarks.jpg": galleryBookmarks,
  "seed:gallery/community-art-table.jpg": galleryCommunityArtTable,
  "seed:gallery/aata-conference.jpg": galleryAataConference,
  "seed:gallery/aata-conference-group.jpg": galleryAataConferenceGroup,
  "seed:gallery/art-and-exhale-pastels.jpg": galleryArtAndExhalePastels,
  "seed:gallery/paper-making.jpg": galleryPaperMaking,
  "seed:gallery/painted-globe.jpg": galleryPaintedGlobe,
  "seed:gallery/felt-hearts.jpg": galleryFeltHearts,
  "seed:gallery/painted-mask.jpg": galleryPaintedMask,
  "seed:gallery/paper-portrait.jpg": galleryPaperPortrait,
  "seed:gallery/beaded-keychains.jpg": galleryBeadedKeychains,
  "seed:gallery/cardboard-octopuses.jpg": galleryCardboardOctopuses,
  "seed:gallery/trees-painting.jpg": galleryTreesPainting,
  "seed:gallery/newspaper-collage-group.jpg": galleryNewspaperCollageGroup,
  "seed:gallery/paper-crown-drawing.jpg": galleryPaperCrownDrawing,
  "seed:downloads/my-safety-plan.pdf": mySafetyPlan,
};

/**
 * A seed media reference as a §3.13.2 media object. The bundle has one JPEG
 * per image, so every variant points at it (browsers sniff the format, so
 * MediaImage's WebP <source> still shows it). A file (the downloads PDFs)
 * gets the file shape; its size is not known in the bundle.
 * @param {string} id - "seed:grounding/<file>.jpg", "seed:gallery/<file>.jpg"
 *   or "seed:downloads/<file>.pdf"
 * @returns {object|null} null for an id with no bundled file
 */
function seedMediaObject(id) {
  const url = SEED_FILES[id];
  const meta = seedMedia.media.find((m) => m.id === id);
  if (!url || !meta) return null;
  if (meta.kind === "file") {
    return {
      id,
      kind: "file",
      content_type: meta.content_type,
      filename: meta.filename,
      size_bytes: null,
      url,
    };
  }
  return {
    id,
    kind: meta.kind,
    content_type: meta.content_type,
    alt: meta.alt,
    width: null,
    height: null,
    url,
    variants: { 800: url, 1600: url, fallback: url },
  };
}

/** Copy of `data` with every {media_id: "seed:…"} replaced by its media object. */
function expandSeedMedia(value) {
  if (Array.isArray(value)) return value.map(expandSeedMedia);
  if (value && typeof value === "object") {
    if (typeof value.media_id === "string") return seedMediaObject(value.media_id);
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, expandSeedMedia(v)]));
  }
  return value;
}

/**
 * The bundled copy of one collection, shaped like the API's items.
 * @param {string} collection
 * @returns {Array<{slug: string, position: number, data: object}>}
 */
export function bundledItems(collection) {
  const seed = SEED[collection];
  if (!seed) return [];
  return seed.items
    .filter((item) => item.is_visible)
    .map((item, i) => ({ slug: item.slug, position: i + 1, data: expandSeedMedia(item.data) }));
}

/** Today's date in Bahamas time as YYYY-MM-DD. */
export function businessDateKey(now = new Date(), timeZone = config.business.timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type) => parts.find((p) => p.type === type).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** True once a YYYY-MM-DD date is behind us in Bahamas time (that day still counts). */
export function isDatePast(dateKey, now = new Date()) {
  return businessDateKey(now) > dateKey;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseIsoDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return { y, m, d };
}

/**
 * A group's `cycle` schedule as "Thursdays, January 21 to February 25, 2027 |
 * 6:00 to 7:30 PM". The year is repeated only when the cycle crosses a year end.
 */
export function formatCycle({ days, start, end, time }) {
  const s = parseIsoDate(start);
  const e = parseIsoDate(end);
  const from = `${MONTHS[s.m - 1]} ${s.d}${s.y === e.y ? "" : `, ${s.y}`}`;
  const to = `${MONTHS[e.m - 1]} ${e.d}, ${e.y}`;
  return `${days}, ${from} to ${to} | ${time}`;
}

/** True once the cycle's last session date is behind us (Bahamas time). */
export function isCyclePast(cycle, now = new Date()) {
  if (!cycle) return true;
  return isDatePast(cycle.end, now);
}

/**
 * Which line a group card shows under its description (contract §3.13.1
 * `schedule`): "next" for the "Next cycle" line, "fallback" for
 * notes.group_cycle_fallback, or null for no line.
 * - cycle: "next" until `end` has passed in Nassau, then "fallback"
 * - none: always "fallback"
 * - ongoing (e.g. Sunday Studio): no line
 */
export function groupCycleLine(schedule, now = new Date()) {
  switch (schedule && schedule.kind) {
    case "cycle":
      return isCyclePast(schedule, now) ? "fallback" : "next";
    case "none":
      return "fallback";
    default:
      return null;
  }
}

/**
 * The `settings` items by slug: {banner, social, notes}. A hidden item is
 * missing from the list, so its key is null.
 * @param {Array} items - settings items (API or bundled)
 */
export function siteSettings(items) {
  const bySlug = Object.fromEntries((items || []).map((item) => [item.slug, item.data || {}]));
  return {
    banner: bySlug.banner || null,
    social: bySlug.social || null,
    notes: bySlug.notes || {},
  };
}

/**
 * The banner to show, or null: none when hidden (absent), without text, or
 * once its `ends_on` date has passed in Nassau (contract §3.11). The API
 * already leaves such a banner out; this covers the bundled copy and a
 * cached response that outlived midnight.
 */
export function activeBanner(banner, now = new Date()) {
  if (!banner || !banner.text) return null;
  if (banner.ends_on && isDatePast(banner.ends_on, now)) return null;
  return banner;
}

const SOCIAL_SITES = [
  { key: "instagram_url", label: "Instagram", host: "instagram.com" },
  { key: "facebook_url", label: "Facebook", host: "facebook.com" },
];

/**
 * The practice's social pages from settings `social`, as [{label, href}].
 * Only https URLs on instagram.com / facebook.com (or a subdomain) are
 * kept, as the contract allows; anything else is dropped.
 */
export function socialLinks(social) {
  if (!social) return [];
  return SOCIAL_SITES.flatMap(({ key, label, host }) => {
    const href = safeHttpsUrl(social[key]);
    if (!href) return [];
    const { hostname } = new URL(href);
    return hostname === host || hostname.endsWith(`.${host}`) ? [{ label, href }] : [];
  });
}
